import React, { useMemo } from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  Checkbox,
  FormHelperText,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { get, sortBy } from "lodash";

const ITEM_HEIGHT = 50;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
    },
  },
};

const SelectAllOptionValue = "all";

const SelectInputBox = ({
  label,
  value,
  options,
  onChange,
  multiple,
  sorted = true,
  fullWidth,
  helperText = "",
  error,
  ...props
}) => {
  const handleChange = (event) => {
    const { value: eventValue } = event.target;
    let newEvent = event;
    if (
      eventValue?.includes(SelectAllOptionValue) &&
      value?.includes(SelectAllOptionValue)
    ) {
      newEvent.target.value = [];
    } else if (
      eventValue?.includes(SelectAllOptionValue) ||
      eventValue?.length === options.length
    ) {
      newEvent.target.value = [SelectAllOptionValue];
    }
    onChange(newEvent);
  };

  const isAllSelected = useMemo(() => {
    if (multiple) {
      return (get(value, "[0]") ?? "") === SelectAllOptionValue;
    }
    return false;
  }, [value, multiple]);

  const optionsList = useMemo(() => {
    return sorted ? sortBy(options, "label") : options;
  }, [sorted, options]);

  return (
    <FormControl fullWidth={fullWidth} size="small">
      <InputLabel error={error}>{label ?? ""}</InputLabel>
      {multiple ? (
        <>
          <Select
            value={
              isAllSelected ? options.map((option) => option.value) : value
            }
            label={label ?? ""}
            onChange={handleChange}
            input={
              <OutlinedInput id="select-multiple-chip" label={label ?? ""} />
            }
            multiple
            multiline
            renderValue={(selected) => {
              const labels = selected.map(
                (value) =>
                  options.find((option) => option.value === value)?.label
              );
              return selected.length === options.length
                ? "All"
                : labels.join(", ");
            }}
            MenuProps={MenuProps}
            error={error}
            {...props}
          >
            {options?.length > 1 && (
              <MenuItem value={SelectAllOptionValue}>
                <Checkbox checked={isAllSelected} size={props.size} />
                <ListItemText primary={"Select All"} />
              </MenuItem>
            )}
            {optionsList?.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                <Checkbox
                  size={props.size}
                  checked={
                    isAllSelected ? true : value.indexOf(item.value) > -1
                  }
                />
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </Select>
          {helperText && (
            <FormHelperText error={error}>{helperText}</FormHelperText>
          )}
        </>
      ) : (
        <>
          <Select
            value={value ?? ""}
            label={label ?? ""}
            onChange={onChange}
            error={error}
            {...props}
          >
            {optionsList?.map((item) => (
              <MenuItem value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
          {helperText && (
            <FormHelperText error={error}>{helperText}</FormHelperText>
          )}
        </>
      )}
    </FormControl>
  );
};

export default SelectInputBox;
